import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useState } from "react";
import { usePermissions } from "../../../../hooks/usePermissions.js";
import {
  APPROVAL_STATUS_MAX_IDS_PER_CALL,
  APPROVAL_STATUS_TARGETS,
  APPROVAL_WRITE_PERMISSION,
  countByStatus,
  type ApprovalRequestStatusRow,
} from "./approvalRequestStatus.js";
import { ApprovalRequestStatusParsedSummary } from "./ApprovalRequestStatusParsedSummary.js";
import { ApprovalRequestStatusRunReport } from "./ApprovalRequestStatusRunReport.js";
import {
  parseApprovalRequestStatusCsv,
  type ParsedApprovalRequestStatusCsv,
} from "./parseApprovalRequestStatusCsv.js";
import { useApprovalRequestStatusBatch } from "./useApprovalRequestStatusBatch.js";
import styles from "../PipelineBrowser.module.css";

const TITLE = "Approval request status from CSV";

type ParsedFile = { name: string; parsed: ParsedApprovalRequestStatusCsv };

function confirmMessage(rows: ApprovalRequestStatusRow[], invalidCount: number): string {
  const counts = countByStatus(rows);
  const breakdown = APPROVAL_STATUS_TARGETS.map(status => `${status}: ${counts[status]}`).join(
    "\n",
  );
  const leftOut = invalidCount > 0 ? `\n\nNot sent: ${invalidCount} invalid rows.` : "";
  return `Decide ${rows.length} approval requests?\n\n${breakdown}${leftOut}\n\nOnly requests currently NEW are decided; the rest come back as skipped. Each decided request sends a callback to the pipeline (approving creates the testimonial). There is no undo.\n\nContinue?`;
}

export function ApprovalRequestStatusCsvSection() {
  const { hasPermission } = usePermissions();
  const [parsing, setParsing] = useState(false);
  const [file, setFile] = useState<ParsedFile | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const { running, stopped, progress, totals, error, start, stop, reset } =
    useApprovalRequestStatusBatch();

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.[0];
      event.target.value = "";
      if (!selected) return;
      reset();
      setParsing(true);
      setParseError(null);
      setFile(null);
      try {
        setFile({ name: selected.name, parsed: await parseApprovalRequestStatusCsv(selected) });
      } catch (err) {
        setParseError(err instanceof Error ? err.message : "Failed to parse CSV");
      } finally {
        setParsing(false);
      }
    },
    [reset],
  );

  const rows = file?.parsed.rows ?? [];
  const invalidCount = file?.parsed.invalid.length ?? 0;
  const canApply =
    !running && !parsing && file !== null && rows.length > 0 && file.parsed.duplicates.length === 0;

  const handleApply = useCallback(() => {
    if (!window.confirm(confirmMessage(rows, invalidCount))) return;
    void start(rows);
  }, [rows, invalidCount, start]);

  if (!hasPermission(APPROVAL_WRITE_PERMISSION)) {
    return (
      <div className={styles.contentSection}>
        <div className={styles.contentSectionTitle}>{TITLE}</div>
        <div className={styles.syncStateMessage}>
          You need the <code>{APPROVAL_WRITE_PERMISSION}</code> permission to decide approval
          requests from a CSV.
        </div>
      </div>
    );
  }

  const hasResult = !running && progress.totalRows > 0;

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>{TITLE}</div>
      <div className={styles.infoBanner}>
        Expected columns: <code>approval_request_id,status</code>. Status must be{" "}
        <code>APPROVED</code>, <code>REJECTED</code> or <code>SUSPENDED</code>. Only requests
        currently <code>NEW</code> are decided: every call carries <code>expectedStatus: NEW</code>{" "}
        and approval-api reports everything else (including ids already in the requested status) as
        skipped, never changing it. There is no dry run; the confirm step shows the counts per
        status before anything is sent. Ids go to approval-api in chunks of{" "}
        {APPROVAL_STATUS_MAX_IDS_PER_CALL} per status; each decided request fires a pipeline
        callback (approving creates the testimonial) and a failed callback reverts the request and
        lands under callback failed. Re-running the same file is safe: decided ids come back as
        skipped.
      </div>

      <div className={styles.syncStateRow}>
        <span className={styles.syncStateLabel}>CSV file:</span>
        <input
          type="file"
          accept=".csv"
          aria-label="Approval request status CSV file"
          disabled={running || parsing}
          onChange={handleFileChange}
        />
      </div>

      {parsing && <div className={styles.syncStateMessage}>Parsing CSV…</div>}
      {parseError && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {parseError}
        </div>
      )}
      {file && <ApprovalRequestStatusParsedSummary parsed={file.parsed} fileName={file.name} />}

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={!canApply}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={!running}
          onClick={stop}
        >
          Stop
        </Button>
        {invalidCount > 0 && (
          <span className={styles.mappingHint}>{invalidCount} invalid rows will be skipped</span>
        )}
      </div>

      {running && (
        <>
          <div className={styles.warningBox}>
            Do not close or refresh this tab and do not navigate away from this page. The decisions
            run from the browser in chunks; leaving stops them mid-way (re-uploading the same file
            is safe, decided ids come back as skipped).
          </div>
          <div className={styles.syncStateMessage} role="status">
            Applying {progress.status ?? ""}: chunk {progress.chunk} / {progress.totalChunks} — ids{" "}
            {progress.processedRows} / {progress.totalRows}
          </div>
          <div className={styles.syncStateMessage}>
            Decided {totals.buckets.decided.length} · skipped not NEW {totals.mismatches.length} ·
            locked {totals.buckets.locked.length} · not found {totals.buckets.notFound.length} ·
            invalid status {totals.buckets.invalidStatus.length} · already{" "}
            {totals.buckets.alreadyInTarget.length} · callback failed{" "}
            {totals.buckets.callbackFailed.length} · unknown {totals.unknown.length}
          </div>
        </>
      )}

      {error && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {error}
        </div>
      )}

      {hasResult && (
        <ApprovalRequestStatusRunReport totals={totals} progress={progress} stopped={stopped} />
      )}
    </div>
  );
}
